import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, GridColumn, GridToolbar, } from "@progress/kendo-react-grid";
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { Button } from "@progress/kendo-react-buttons";
import Dialog from '../../components/Dialog/Dialog';
import { process } from '@progress/kendo-data-query';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { Input } from '@progress/kendo-react-inputs';
import { CheckmarkCell, DateCell, ApplicationCell, EditCommandCell, GroupCell } from '../../components/GridCell'
import { setCurrentApplication } from '../../store/actions/applicationActions';
import { deleteApplication, updateApplication } from '../../actions/portal/applications';
import { formCloser } from '../../utils/formCloser';

const initialDataState = {
  skip: 0,
  take: 10
}

const DashboardApplications = () => {
  const _export = React.useRef(null);

  const { token, user } = useSelector(state => state.authReducer);
  const { applications } = useSelector(state => state.applicationReducer);

  const [dataState, setDataState] = useState(initialDataState);
  const [dialogProps, setDialogProps] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showSearchClearBtn, setShowSearchClearBtn] = useState(false);

  const filteredApplications = applications.map(a => a).sort((a, b) => a.approved || a.denied ? 1 : -1)
    .filter(a => {
      if (!searchInput || !searchInput.length) {
        return true;
      }
      return (a.first_name && a.first_name.toLowerCase().includes(searchInput.toLowerCase())) || 
        (a.last_name && a.last_name.toLowerCase().includes(searchInput.toLowerCase())) ||
        (a.email_address && a.email_address.toLowerCase().includes(searchInput.toLowerCase()))
      })

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const enterEdit = (target) => {
    dispatch(setCurrentApplication(target));
    navigate('/application')
  }

  const approve = (target) => {
    submitApplication(target, true);
  }

  const deny = (target) => {
    submitApplication(target, false);
  }

  const deleteEntry = (dataItem) => {
    // prompt for confirmation
    setDialogProps({
      title: `Delete Application`,
      text: `Are you sure you want to delete ${dataItem.email_address}?`,
      onConfirm: () => {
        dispatch(deleteApplication(dataItem.id, token, formCloser(setShowDialog)))
      },
      enableCancelBtn: true
    });

    setShowDialog(true);
  }

  const submitApplication = (target, approved) => {
    let applicationData = {
      first_name: target.first_name,
      position: target.position,
      work_phone: target.work_phone,
      city: target.city,
      state: target.state,
      county: target.county,
      last_name: target.last_name,
      mobile_phone: target.mobile_phone,
      zipcode: target.zipcode,
      email_address: target.email_address,
      agency_name: target.agency_name,
      orgdescription: target.orgdescription,
      sworn: target.sworn,
      needs: target.needs,
      enabled: approved ? true : false,
      approved: approved ? true : false,
      denied: approved ? false : true,
      registration_type: target.registration_type,
      region: target.region,
      approved_by: target.approved_by,
      denied_by: target.denied_by,
      approved_date: target.approved_date,
      denied_date: target.denied_date,
      internal_notes: target.internal_notes,
      editorId: user.id
    }
    dispatch(updateApplication(target.id, applicationData, token, () => {navigate('/dashboard')}));
  }

  return (
    <div>
      <ExcelExport ref={_export} data={dataState}>
        <Grid
          {...dataState}
          data={filteredApplications ? process(filteredApplications, dataState) : null}
          onDataStateChange={e => setDataState(e.dataState)}
          pageable={true}
          sortable={true}
          className='flo-application-grid'
        >
          <GridToolbar>
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <div 
                  style={{
                    paddingLeft: 30,
                  }}
                >
                  <FloatingLabel
                    label='Search'
                    editorId='search-input'
                    editorValue={searchInput}
                  >
                    <Input
                      id='search-input'
                      value={searchInput}
                      onChange={e => {
                        setSearchInput(e.value)
                        //setDataState({
                        //  ...dataState,
                        //  filter: {
                        //    logic: 'or',
                        //    filters: [
                        //      'email',
                        //      'firstName',
                        //      'lastName',
                        //      'agency',
                        //    ].map(field => {
                        //      return {
                        //        field,
                        //        operator: 'contains',
                        //        value: e.value
                        //      }
                        //    })
                        //  }
                        //});

                        setShowSearchClearBtn(e.value ? true : false);
                      }}
                    />
                  </FloatingLabel>
                  {showSearchClearBtn &&
                    <Button
                      className="k-button k-primary"
                      iconClass="k-icon k-i-close"
                      onClick={() => {
                        setDataState(initialDataState);
                        setShowSearchClearBtn(false);
                        setSearchInput('');
                      }}
                    />}
                </div>
              </div>
              <div>
                <Button
                  className="k-button"
                  style={{
                    minHeight: 25,
                    padding: '10px 20px',
                    marginRight: 15,
                  }}
                  onClick={excelExport}
                >
                  Export to Excel
                </Button>
              </div>
            </div>
          </GridToolbar>
          <GridColumn field="first_name" title="First Name" />
          <GridColumn field="last_name" title="Last Name" />
          <GridColumn field="email_address" title="Email" />
          <GridColumn field="agency_name" title="Agency" />
          <GridColumn
            title="Approved"
            field="approved"
            cell={props => <CheckmarkCell checked={props.dataItem.approved} />}
            width={120}
          />
          <GridColumn
            title="Denied"
            field="denied"
            cell={props => <CheckmarkCell checked={props.dataItem.denied} />}
            width={120}
          />
          <GridColumn
            title="Enabled"
            field="enabled"
            filter="boolean"
            cell={props => <CheckmarkCell checked={props.dataItem.enabled} />}
            width={120}
          />
          <GridColumn
            title="Created"
            field="createdAt"
            cell={props => <DateCell date={props.dataItem.createdAt} />}
            width={150}
          />
          <GridColumn
            title="Updated"
            field="updatedAt"
            cell={props => <DateCell date={props.dataItem.updatedAt} />}
            width={150}
          />
          <GridColumn
            cell={(props) => (
              <EditCommandCell
                {...props}
                user={props.dataItem}
                enterEdit={enterEdit}
                deleteEntry={(dataItem) => deleteEntry(dataItem)}
              />
            )}
            width={100}
          />
          <GridColumn
            cell={(props) => (
              <ApplicationCell
                approve={approve}
                deny={deny}
                {...props}
                //enterEdit={enterEdit}
                //deleteEntry={(dataItem) => deleteEntry(dataItem)}
              />
            )}
            width={150}
          />
        </Grid>
      </ExcelExport>
      {showDialog &&
        <Dialog
          {...dialogProps}
          onClose={() => setShowDialog(false)}
        />}
    </div>
  )
}

export default DashboardApplications;